import { useCallback, useEffect, useRef } from 'react';

export type FlyerLaunch = { from: DOMRect; delta: number };

// jsdom (this project's pinned test environment) has no `matchMedia` at all by default, hence the
// optional chaining — same idiom ShellsHud.tsx/MapPanZoom.tsx/WorldScreen.tsx already use.
const prefersReducedMotion = () =>
  typeof window !== 'undefined'
  && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true;

// `Element.animate` (the Web Animations API) cannot consume a live CSS `var()` reference, so the
// travel animation's duration/easing are read once, at launch, from the SAME tokens base.css's
// own `.coin-flyer-fallback` keyframe uses — not duplicated as bare numbers here. This is the
// established idiom for a JS-driven animation to stay token-compliant (see
// `minigames/engine/useGameCanvas.ts`'s `readCanvasColors`, doing the same read-once-via-
// getComputedStyle thing for a 2D canvas context's fill colours, which likewise cannot take a
// live `var()`). The `* 2.75` multiplier mirrors base.css's own `.coin-flyer-fallback` rule
// exactly — if that number changes there, change it here too.
const TRAVEL_DURATION_MULTIPLIER = 2.75;

function travelTiming(): { duration: number; easing: string } {
  const computed = getComputedStyle(document.documentElement);
  const base = parseFloat(computed.getPropertyValue('--t-enter')) || 0;
  const easing = computed.getPropertyValue('--ease-enter').trim() || 'ease';
  return { duration: base * TRAVEL_DURATION_MULTIPLIER, easing };
}

/**
 * One currency-delta chip travelling from where the money was spent or found to the Shells chip.
 * This is what makes §6.1 legal under build-prompt §11.3's "motion must explain a spatial
 * relationship or it doesn't exist" — a chip that merely bumps is decoration; a token flying to
 * the wallet is an explanation. This is also the difference the owner picked C3 ("count-up +
 * flyer") over C2 ("count-up + bump") for: a float-up-and-fade in place explains only the origin,
 * never the destination.
 *
 * §11.3 also binds "never optimistic on anything that moves currency" — so a launch only ever
 * happens on the SERVER's response, by which point the shop's detail sheet has usually closed and
 * the source cell is gone. `launch` therefore takes a plain `DOMRect`, never an element or a ref:
 * the rect is captured by the caller (typically inside a mutation's `onSuccess`, reading the
 * source element's `getBoundingClientRect()` a moment before it unmounts) and the flyer holds no
 * reference to anything that might disappear. The DESTINATION, by contrast, is resolved fresh at
 * launch time from the live DOM (`document.querySelector('.hud-currency')`) — never cached, never
 * assumed — because the chip's own position can change between renders and, unlike the source, it
 * is still on screen at the moment the flight needs it.
 *
 * A CSS keyframe cannot express a journey to a screen position it doesn't know at author time, so
 * the actual travel is driven imperatively via `Element.animate` (the Web Animations API) — three
 * keyframes (start, a small overshoot at 28%, the destination), matching
 * `docs/demos/motion-demo.html` §5's `data-coin` handler, the owner-approved reference for this
 * exact behaviour. Two things can make that impossible, and both degrade to the OLD float-and-fade
 * in place (still shows the delta happened, just without the travel explanation) rather than
 * silently dropping the delta:
 *  - no `.hud-currency` chip is present at all (the wardrobe tab hides the whole HUD, and might
 *    launch a flyer of its own some day);
 *  - `Element.animate` itself is absent — true of jsdom (this project's pinned test environment),
 *    which is why every existing test exercises this fallback path, never the real WAAPI one.
 * Reduced motion is checked BEFORE either of those and always wins: no flight at all, just a
 * cross-fade in place (§11.3) — this has to be a JS-side check, not left to a CSS media query,
 * because `Element.animate` calls are themselves JS and a `prefers-reduced-motion` media query has
 * no power to stop one from running.
 *
 * Each flight is a self-contained DOM node appended straight to `document.body` and removed when
 * it finishes — via `animationend` for the two CSS-driven paths (fallback and reduced-motion), via
 * the WAAPI `Animation`'s own `onfinish` for the real travel. It is NOT a React-rendered/
 * `createPortal` element: that would require whatever consumed `launch` to also render a returned
 * node somewhere in ITS OWN tree, reintroducing exactly the lifetime coupling the rect-only
 * contract exists to avoid (the consumer that calls `launch` is usually the very component whose
 * sheet is about to close).
 *
 * A flight that never finishes naturally is still cleaned up: this hook tracks every element it
 * has appended and removes any survivors when the component OWNING THE HOOK unmounts. That is a
 * real removal, not a no-op — a flight is only ever independent of the SOURCE element's lifetime
 * (that's the whole reason `launch` takes a rect, not a ref), never of its own caller's. Whatever
 * wires `launch()` into a real purchase/forage flow must therefore call `useCoinFlyer` from a
 * persistent, shell-level component (e.g. something mounted alongside `ShellsHud` itself, not
 * inside an ephemeral shop sheet or bag cell) — otherwise navigating away mid-flight cuts the
 * flyer off before it ever reaches the wallet. That is why this hook has exactly ONE call site,
 * `AppShell`, and why the tap-time rect reaches it through `flyerQueue.ts` rather than being
 * handed straight from the shop sheet that read it — see that module's own header comment.
 */
export function useCoinFlyer(): { launch: (opts: FlyerLaunch) => void } {
  const liveRef = useRef<Set<HTMLSpanElement>>(new Set());

  useEffect(() => {
    const live = liveRef.current;
    return () => {
      live.forEach((el) => el.remove());
      live.clear();
    };
  }, []);

  const launch = useCallback((opts: FlyerLaunch) => {
    const { from, delta } = opts;
    const gain = delta > 0;
    const el = document.createElement('span');
    el.setAttribute('data-testid', 'coin-flyer');
    el.setAttribute('aria-hidden', 'true');
    el.className = `coin-flyer ${gain ? 'coin-flyer-gain' : 'coin-flyer-spend'}`;
    el.style.position = 'fixed';
    const sourceX = from.left + from.width / 2;
    const sourceY = from.top;
    el.style.left = `${sourceX}px`;
    el.style.top = `${sourceY}px`;
    el.style.pointerEvents = 'none';
    el.textContent = `${gain ? '+' : '−'}${Math.abs(delta)}`;

    const live = liveRef.current;
    live.add(el);
    const finish = () => { el.remove(); live.delete(el); };

    document.body.appendChild(el);

    if (prefersReducedMotion()) {
      el.classList.add('coin-flyer-reduced');
      el.addEventListener('animationend', finish, { once: true });
      return;
    }

    const dest = document.querySelector<HTMLElement>('.hud-currency');
    if (dest == null || typeof el.animate !== 'function') {
      el.classList.add('coin-flyer-fallback');
      el.addEventListener('animationend', finish, { once: true });
      return;
    }

    // Task 6's deferred minor, closed now that this hook has real call sites. The element is
    // ALREADY appended by this point (it has to be — the two degraded paths above rely on it being
    // in the document so their CSS animation runs and fires `animationend`), so a throw anywhere in
    // this block would strand a visible, permanently un-animated delta chip over the app until the
    // owning component unmounted. Every statement here can genuinely throw on some engine:
    // `getComputedStyle`/`getBoundingClientRect` on a detached or mid-teardown document, and
    // `Element.animate` itself, which is spec'd to throw `TypeError` on a keyframe list an
    // implementation cannot parse and `NotSupportedError` on an unsupported easing string — the
    // easing here is read from a CSS custom property, so a token edit that produced an invalid
    // value would land exactly there. The recovery is the SAME degradation the two guarded
    // branches above already use — the float-and-fade in place, which still shows the delta — not a
    // silent drop and not a stranded node.
    try {
      const destRect = dest.getBoundingClientRect();
      const dx = destRect.left + destRect.width / 2 - sourceX;
      const dy = destRect.top + destRect.height / 2 - sourceY;
      const { duration, easing } = travelTiming();

      const animation = el.animate(
        [
          { transform: 'translate(0, 0) scale(0.85)', opacity: 0 },
          { transform: `translate(${dx * 0.35}px, ${dy * 0.18}px) scale(1.12)`, opacity: 1, offset: 0.28 },
          { transform: `translate(${dx}px, ${dy}px) scale(0.7)`, opacity: 0 },
        ],
        { duration, easing },
      );
      animation.onfinish = finish;
    } catch {
      el.classList.add('coin-flyer-fallback');
      el.addEventListener('animationend', finish, { once: true });
    }
  }, []);

  return { launch };
}
